import { type FormEvent, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';

import { WelcomeLayout } from '@/components/auth/WelcomeLayout';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { useJoinableOrgs } from '@/lib/accountRequests';
import { api } from '@/lib/api';
import { useMe } from '@/lib/auth';
import { usePublicSettings } from '@/lib/settings';

import { AuthCard, FormField } from './LoginPage';

interface SubmitResponse {
  detail: string;
}

/**
 * Request-account form, only meaningful when `signup_mode === 'request'`.
 * Other modes redirect: 'open' sends the user to /signup, 'closed'
 * sends them to /login (with the closed-message rendering there).
 *
 * The form posts to `/api/auth/request-account`, which always returns
 * 202 (whether the row was persisted, honeypotted, or rate-limited).
 * That ambiguity is intentional — it's a spam guard. So the success
 * UI is unconditionally optimistic.
 */
export function RequestAccountPage() {
  const me = useMe();
  const settings = usePublicSettings();
  const joinableOrgs = useJoinableOrgs();

  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  // Empty string = "no preference / let site admin route me". A real
  // numeric value sends the request to that org's owners + admins
  // and pins the eventual approval to that org only.
  const [orgId, setOrgId] = useState<number | ''>('');
  const [reason, setReason] = useState('');
  // Honeypot — bots auto-fill every field; humans don't see this one
  // (it's CSS-hidden below). A populated value tells the backend to
  // drop the request silently.
  const [website, setWebsite] = useState('');

  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (me.data) return <Navigate to="/" replace />;
  if (settings.data && settings.data.signup_mode === 'open') {
    return <Navigate to="/signup" replace />;
  }
  if (settings.data && settings.data.signup_mode === 'closed') {
    return <Navigate to="/login" replace />;
  }

  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      await api.postJson<SubmitResponse>('/api/auth/request-account', {
        email,
        display_name: name.trim() || null,
        requested_org_id: orgId === '' ? null : orgId,
        reason: reason.trim() || null,
        website,
      });
      setSubmitted(true);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <WelcomeLayout>
        <AuthCard
          standalone={false}
          title="Request received"
          subtitle="Thanks — an admin will review this and email you with the outcome."
        >
          <p className="text-center text-sm text-stone-600 dark:text-stone-400">
            <Link
              to="/login"
              className="font-medium text-forge-700 dark:text-forge-400 hover:underline"
            >
              Back to sign in
            </Link>
          </p>
        </AuthCard>
      </WelcomeLayout>
    );
  }

  const customMessage = settings.data?.signup_disabled_message;

  return (
    <WelcomeLayout>
      <AuthCard
        standalone={false}
        title="Request an account"
        subtitle={
          customMessage ??
          "Fill in a few details — an admin will review and email you back."
        }
      >
        <form onSubmit={onSubmit} className="space-y-4">
          <FormField label="Email">
            <Input
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </FormField>
          <FormField label="Display name (optional)">
            <Input
              type="text"
              autoComplete="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </FormField>
          <FormField label="Workshop to join (optional)">
            <select
              value={orgId}
              onChange={(e) =>
                setOrgId(e.target.value === '' ? '' : Number(e.target.value))
              }
              className="block w-full rounded-md border border-stone-300 dark:border-stone-700 bg-white dark:bg-stone-900 px-3 py-2 text-sm focus:border-forge-500 focus:outline-none focus:ring-1 focus:ring-forge-500"
            >
              <option value="">— no preference (site admin routes) —</option>
              {joinableOrgs.data?.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.name}
                </option>
              ))}
            </select>
            <p className="mt-1 text-xs text-stone-500 dark:text-stone-400">
              Picking a workshop sends the request to that workshop's
              admins. Otherwise it goes to the site admin.
            </p>
          </FormField>
          <FormField label="Anything the admin should know (optional)">
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              maxLength={500}
              className="block w-full rounded-md border border-stone-300 dark:border-stone-700 bg-white dark:bg-stone-900 px-3 py-2 text-sm focus:border-forge-500 focus:outline-none focus:ring-1 focus:ring-forge-500"
            />
          </FormField>

          {/* Honeypot field — hidden from real users, automated bots
              fill it in. The `tabIndex={-1}` and `autoComplete="off"`
              both nudge browsers into not surfacing it via password
              managers etc. */}
          <div
            aria-hidden
            className="absolute left-[-10000px] top-auto h-px w-px overflow-hidden"
          >
            <label>
              Don't fill this out
              <input
                type="text"
                tabIndex={-1}
                autoComplete="off"
                value={website}
                onChange={(e) => setWebsite(e.target.value)}
              />
            </label>
          </div>

          {error && <p className="text-sm text-red-600">{error}</p>}

          <Button type="submit" className="w-full" disabled={submitting}>
            {submitting ? 'Submitting…' : 'Submit request'}
          </Button>
        </form>
        <p className="mt-6 text-center text-sm text-stone-600 dark:text-stone-400">
          Already have an account?{' '}
          <Link
            to="/login"
            className="font-medium text-forge-700 dark:text-forge-400 hover:underline"
          >
            Sign in
          </Link>
        </p>
      </AuthCard>
    </WelcomeLayout>
  );
}
